/**
 * Sound and the opening ceremony, in the menu rather than beside the hand.
 *
 * Both used to sit on the rack, one row under the player's tiles, which put a
 * checkbox and a dropdown inside the most important object on the screen. They
 * are preferences: set once, changed rarely, and worth nothing during a turn.
 *
 * Each control writes straight through to its own store, so nothing here has
 * to be threaded back up through the table. The opening preference is read
 * again when a new round starts, which is the only moment it can matter.
 */

import { useState } from "react";
import { useI18n } from "../i18n";
import { isSoundOn, setSoundOn } from "../game/tableAudio";
import {
  persistOpeningPreference,
  readOpeningPreference,
} from "../game/openingPreference";
import type { OpeningPreference } from "../game/openingPreference";

export interface TableSettingsProps {
  /** Tutorial has no opening ceremony, so it has nothing to prefer about one. */
  showOpening?: boolean;
}

export function TableSettings({ showOpening = true }: TableSettingsProps) {
  const { t } = useI18n();
  const [soundOn, setSound] = useState(isSoundOn);
  const [opening, setOpening] = useState(readOpeningPreference);

  return (
    <section className="settings" data-testid="table-settings">
      <h3 className="sheet__heading">{t("shell.section.settings")}</h3>

      <label className="settings__item">
        <input
          type="checkbox"
          checked={soundOn}
          onChange={(event) => {
            const on = event.target.checked;
            setSound(on);
            setSoundOn(on);
          }}
        />
        <span>{t("sound.preference")}</span>
      </label>

      {showOpening ? (
        <label className="settings__item settings__item--select">
          <span>{t("opening.preference")}</span>
          <select
            value={opening}
            onChange={(event) => {
              const value = event.target.value as OpeningPreference;
              setOpening(value);
              persistOpeningPreference(value);
            }}
          >
            <option value="full">{t("opening.full")}</option>
            <option value="fast">{t("opening.fast")}</option>
            <option value="off">{t("opening.off")}</option>
          </select>
        </label>
      ) : null}
    </section>
  );
}
